import { ValidatorFn, AbstractControl, ValidationErrors, FormGroup } from '@angular/forms';
import { PipelineType } from './model/general/PipelineType';
import { DateUtil } from './DateUtil';

export class ValidatorUtil {
  public static readonly MIN_YEAR = 1980;
  public static readonly MAX_YEAR = 3000;

  public static readonly DATE_INPUT_ERRORS: Record<string, string> = {
    missing: 'Please enter a date',
    invalid: 'Entered date is invalid',
    minDate: 'Enter a year after 1980',
    maxDate: 'Enter a year before 3000',
  };

  public static createProjectAcquiredDateValidator(
    ctrlNamePipelineType: string,
    ctrlNameAcquiredDate: string,
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const formGroup = group as FormGroup;
      const ctrlPipelineType = formGroup.get(ctrlNamePipelineType);
      const ctrlAcquiredDate = formGroup.get(ctrlNameAcquiredDate);

      if (!ctrlAcquiredDate) return null;

      const dateErrors = ValidatorUtil.validateDate(ctrlAcquiredDate);

      const isAcquisition = ctrlPipelineType?.value === PipelineType.ACQUISITION;
      const isPresent = !!ctrlAcquiredDate.value;
      const missingError = isAcquisition && !isPresent ? { missing: true } : null;

      const combinedErrors = { ...dateErrors, ...missingError };

      const hasErrors = Object.keys(combinedErrors).length > 0;
      ctrlAcquiredDate.setErrors(hasErrors ? combinedErrors : null);

      return null;
    };
  }

  public static validateDate(formCtrl: AbstractControl): ValidationErrors | null {
    if (!formCtrl.value) {
      return null;
    }
    const date = new Date(formCtrl.value);
    if (!date || isNaN(date.getFullYear())) {
      return { invalid: formCtrl.value };
    } else if (date.getFullYear() < ValidatorUtil.MIN_YEAR) {
      return { minDate: true };
    } else if (date.getFullYear() > ValidatorUtil.MAX_YEAR) {
      return { maxDate: true };
    }
    return null;
  }

  public static validateMandatory(formCtrl: AbstractControl): ValidationErrors | null {
    if (!formCtrl?.value) {
      return { missing: true };
    }
    return null;
  }

  public static createMilestoneValidator(mandatory: boolean): ValidatorFn {
    return (formCtrl: AbstractControl): ValidationErrors | null => {
      if (!formCtrl) {
        return null;
      }
      const dateErrors = ValidatorUtil.validateDate(formCtrl);

      let mandatoryErrors = null;
      if (mandatory) {
        mandatoryErrors = ValidatorUtil.validateMandatory(formCtrl);
      }
      if (!dateErrors && !mandatoryErrors) {
        return null;
      }

      return {
        ...dateErrors,
        ...mandatoryErrors,
      };
    };
  }

  public static getErrorMessage(control: AbstractControl | null): string {
    if (!control?.errors) {
      return '';
    }

    const errorKey = Object.keys(control.errors).find((key) => !!this.DATE_INPUT_ERRORS[key]);

    return errorKey ? this.DATE_INPUT_ERRORS[errorKey] : '';
  }

  public static getClampedDate(value: any): Date | null {
    if (!value) return null;

    const date = value instanceof Date ? value : DateUtil.anyToDate(value);
    const year = date.getFullYear();

    if (isNaN(year)) return null;

    if (year < this.MIN_YEAR) {
      return new Date(Date.UTC(this.MIN_YEAR, 0, 1));
    }

    if (year > this.MAX_YEAR) {
      return new Date(Date.UTC(this.MAX_YEAR, 11, 31));
    }

    return date;
  }

  public static getClampedDateString(value: any): string | null | undefined {
    const clamped = this.getClampedDate(value);

    if (clamped === undefined) return undefined;
    if (clamped === null) return null;

    return DateUtil.ddmmyyyy(clamped);
  }
}
